import { ApiCollection }                  from 'Collections/ApiCollection';
import CompanyAddressModel                from 'Models/directory/CompanyAddressModel';
import CompanyContactModel                from 'Models/directory/CompanyContactModel';
import CompanyCountryCompanyPropertyModel from 'Models/directory/CompanyCountryCompanyPropertyModel';
import CompanyModel                       from 'Models/directory/CompanyModel';
import CompanyPropertyModel               from 'Models/directory/CompanyPropertyModel';
import CountryCompanyPropertyModel        from 'Models/directory/CountryCompanyPropertyModel';
import By                                 from 'components/By';
import { IGridInfoItem }                  from 'components/GridInfo/GridInfo';
import Link                               from 'components/Link';
import React                              from 'react';
import AbstractModelXStore                from 'stores/AbstractModelXStore';
import { chunkArray }                     from 'tools/arrayTools';
import navigate                           from 'tools/navigate';
import { formatByInterval }               from 'tools/stringTools';

type GetCompanyPropertyValue = {
	(
		companyUrn: Urn,
		reference: DirectoryCompanyPropertyReference,
	): string | undefined;
	(
		companyUrn: Urn,
		reference: DirectoryCompanyPropertyReference,
		mode: 'raw'
	): string | undefined;
	(
		companyUrn: Urn,
		reference: DirectoryCompanyPropertyReference,
		mode: 'formatted'
	): React.ReactElement | string | undefined;
};

export default class CompanyPropertiesStore extends AbstractModelXStore {
	private companyAddressCollection = new ApiCollection(CompanyAddressModel);
	private companyCollection = new ApiCollection(CompanyModel);
	private companyContactCollection = new ApiCollection(CompanyContactModel);
	private companyCountryCompanyPropertyCollection = new ApiCollection(CompanyCountryCompanyPropertyModel);
	private companyPropertyCollection = new ApiCollection(CompanyPropertyModel);
	private countryCompanyPropertyCollection = new ApiCollection(CountryCompanyPropertyModel);
	private loadedCompanyForGridInfoIds: string[] = [];
	private loadedCompanyIds: string[] = [];
	private loadedCountryCompanyPropertyIds: string[] = [];
	private loadingCompanyForGridInfoIds: string[] = [];
	private loadingCompanyIds: string[] = [];

	public getCompanyAddress = (companyUrn: Urn) => {
		return this.companyAddressCollection.find(c => c.getUrn('company') === companyUrn);
	};

	public getCompanyContact = (companyUrn: Urn) => {
		return this.companyContactCollection.find(c => c.getUrn('company') === companyUrn);
	};

	public getCompanyCountryCompanyProperty = (
		companyUrn: Urn,
		reference: DirectoryCompanyPropertyReference,
	) => {
		const companyProperty = this.companyPropertyCollection
			.find(ep => ep.reference === reference);

		const companyCccps = this.companyCountryCompanyPropertyCollection
			.filter(cccp => cccp.getUrn('company') === companyUrn);

		return companyCccps.find(cccp => {
			return this.countryCompanyPropertyCollection
				.some(ccp =>
					cccp.getId('countryCompanyProperty') === ccp.id
					&& ccp.getId('companyProperty') === companyProperty?.id
				);
		});
	};

	public getCompanyGridInfoData = (
		companyUrn: Urn,
		showName?: boolean,
		showEnterpriseName?: boolean,
		numberOfLines?: number
	) => {
		const company = this.companyCollection.find(c => c.urn === companyUrn);

		if (!company) {
			return [];
		}

		const companyAddress = this.getCompanyAddress(companyUrn);
		const companyContact = this.getCompanyContact(companyUrn);

		const defaultGridInfoData: IGridInfoItem[] = [
			...showName ? [{
				label: 'Dénomination',
				value: <Link to={navigate.toCompany(company.id)}>{company.name}</Link>,
			}] : [],
			...showEnterpriseName ? [{
				label: `Entreprise de l'établissement`,
				value: <Link to={navigate.toEnterprise(company.enterprise.id)}>{company.enterprise.name}</Link>,
			}] : [],
			{
				label: `Pays`,
				type: 'country',
				value: company.enterprise?.country,
			},
			{
				label: 'Siège social',
				type: 'boolean',
				value: company.headOffice,
			},
			{
				label: 'Adresse',
				value: companyAddress?.fullAddress,
			},
			{
				label: `Complément d'adresse`,
				value: companyAddress?.addressBis,
			},
			{
				label: 'Téléphone',
				type: 'phone',
				value: companyContact?.phoneNumber,
			},
			{
				label: 'Fax',
				type: 'phone',
				value: companyContact?.faxNumber,
			},
			{
				label: 'E-mail',
				type: 'email',
				value: companyContact?.email,
			},
		];

		const gridInfoData: IGridInfoItem[] = [...defaultGridInfoData];

		const companyCccps = this.companyCountryCompanyPropertyCollection
			.filter(cccp => cccp.getUrn('company') === companyUrn);

		companyCccps.forEach(cccp => {
			const countryCompanyProperty = this.countryCompanyPropertyCollection
				.find(ccp => ccp.id === cccp.getId('countryCompanyProperty'));
			const companyProperty = this.companyPropertyCollection
				.find(cp => cp.id === countryCompanyProperty?.getId('companyProperty'));

			const value = companyProperty
				? this.formatValue(cccp.value, companyProperty.reference)
				: undefined;

			gridInfoData.push({
				label: companyProperty?.label,
				value: value,
			});
		});

		gridInfoData.push({ label: 'Établissement créé', value: <By model={company} /> });

		if (numberOfLines) {
			return chunkArray(gridInfoData, numberOfLines);
		}
		return [gridInfoData];
	};

	public getCompanyPropertyValue: GetCompanyPropertyValue = (
		companyUrn: Urn,
		reference: DirectoryCompanyPropertyReference,
		mode: 'raw' | 'formatted' = 'raw',
	) => {

		const value = this.getCompanyCountryCompanyProperty(companyUrn, reference)?.value;

		if (!value) {
			return undefined;
		}

		if (mode === 'raw') {
			return value;
		}

		return this.formatValue(value, reference);
	};

	public async initAsync(companyIds: id[], setForGridInfo?: boolean) {
		this.clear();

		this.loadingCompanyIds = this.idsToStringArray(companyIds);
		this.setIsLoading(true);

		if (setForGridInfo) {
			this.loadingCompanyForGridInfoIds = this.idsToStringArray(companyIds);
			await this.companyCollection.listBy(companyIds);

			await Promise.all([
				this.companyCollection.whenIsLoaded(c => c.enterprise),
				this.companyAddressCollection.listBy(this.companyCollection.ids, 'company'),
				this.companyContactCollection.listBy(this.companyCollection.ids, 'company'),
			]);

			this.loadedCompanyForGridInfoIds = this.idsToStringArray(companyIds);
			this.loadingCompanyForGridInfoIds = this.loadingCompanyForGridInfoIds
				.filter(id => !this.loadedCompanyForGridInfoIds.includes(id.toString()));
		}

		await this.companyPropertyCollection.list();
		await this.companyCountryCompanyPropertyCollection.listBy(companyIds, 'company');
		const countryCompanyPropertyIds = this.companyCountryCompanyPropertyCollection
			.distinctIds('countryCompanyProperty');
		await this.countryCompanyPropertyCollection.listBy(countryCompanyPropertyIds, 'id');

		this.loadedCompanyIds = [ ...this.loadedCompanyIds, ...this.idsToStringArray(companyIds)];
		this.loadingCompanyIds = this.loadingCompanyIds
			.filter(id => !this.loadedCompanyIds.includes(id.toString()));
		this.loadedCountryCompanyPropertyIds = [
			...this.loadedCountryCompanyPropertyIds,
			...this.idsToStringArray(this.countryCompanyPropertyCollection.ids)
		];
		this.setIsLoading(false);
	}

	public async initOrLoadMoreAsync(companyIds: id[]) {
		if (!this.companyCountryCompanyPropertyCollection.length && !this.loadingCompanyIds.length) {
			return this.initAsync(companyIds, false);
		}

		this.loadingCompanyIds = [ ...this.loadingCompanyIds, ... this.idsToStringArray(companyIds)];
		this.setIsLoading(true);

		const filteredCompanyIds = companyIds
			.filter(companyId => !this.loadedCompanyIds.includes(companyId.toString()));

		const collCompanyCountryCompanyProperty = new ApiCollection(CompanyCountryCompanyPropertyModel);
		await collCompanyCountryCompanyProperty.listBy(filteredCompanyIds, 'company');

		const filteredCcpIds = collCompanyCountryCompanyProperty
			.distinctIds('countryCompanyProperty')
			.filter(id => !this.loadedCountryCompanyPropertyIds.includes(id.toString()));
		const collCountryCompanyProperty = new ApiCollection(CountryCompanyPropertyModel);
		await collCountryCompanyProperty.listBy(filteredCcpIds, 'id');

		this.companyCountryCompanyPropertyCollection.set([
			...this.companyCountryCompanyPropertyCollection.models,
			...collCompanyCountryCompanyProperty.models,
		]);

		this.countryCompanyPropertyCollection.set([
			...this.countryCompanyPropertyCollection.models,
			...collCountryCompanyProperty.models,
		]);

		this.loadedCompanyIds = [...this.loadedCompanyIds, ...this.idsToStringArray(filteredCompanyIds)];
		this.loadingCompanyIds = this.loadingCompanyIds.filter(id => !this.loadedCompanyIds.includes(id.toString()));
		this.loadedCountryCompanyPropertyIds = [ ...this.loadedCountryCompanyPropertyIds, ...this.idsToStringArray(filteredCcpIds)];
		this.setIsLoading(false);
	}

	private formatValue = (value: string, reference: DirectoryCompanyPropertyReference) => {
		switch (reference) {
			case 'siret':
				return formatByInterval(value, 3);
			default:
				return value;
		}
	};

	private idsToStringArray(arr: id[]): string[] {
		return arr.map(id => id.toString());
	}

}
